import React, { MouseEventHandler } from 'react';
import { styles } from './CarouselLeadVideo.css';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { ProductCarousel, ProductCarouselProps } from '../ProductCarousel/ProductCarousel';
import AspectRatio from '@jbhi-fi/lanyard-ui/components/AspectRatio';
import { useTrackingContext } from '../Tracking';
import { GoToLink, GoToLinkProps } from '../GoToLink/GoToLink';
import { ContentfulYoutube } from '@jbhi-fi/apis/src/contentful/types';
import { contentfulYoutubeToYoutubePlayerProps } from '../Contentful/transformers/contentfulYouTubeToYouTubePlayerProps';
import { ASPECT_RATIOS, PLAYER_STATE, YoutubePlayer } from '../YouTubePlayer/YouTubePlayer';

export type CarouselLeadVideoProps = {
  video?: ContentfulYoutube;
  desktopCoverImage?: React.ReactElement;
  mobileCoverImage?: React.ReactElement;
  linkProps?: GoToLinkProps;
  ctaButton?: React.ReactNode;
  productCarouselProps: ProductCarouselProps;
};

export const CarouselLeadVideo = ({
  video,
  linkProps,
  ctaButton,
  productCarouselProps,
  desktopCoverImage,
  mobileCoverImage,
}: CarouselLeadVideoProps) => {
  const isDesktop = useBreakpoint('md', 'up');
  const { tracker } = useTrackingContext();
  if (!tracker.isEmpty) {
    tracker.group = tracker.group ?? productCarouselProps.trackingListName ?? productCarouselProps.heading;
  }

  const handleVideoPlayPause = (event: { data: number }) => {
    if (['play', 'pause'].includes(PLAYER_STATE[event.data])) {
      tracker.trackClick({
        label: video?.fields?.name,
        extraData: {
          mediaType: 'video',
          mediaAction: PLAYER_STATE[event.data],
          moduleName: 'Carousel lead video',
        },
      });
    }
  };

  const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = e => {
    tracker.trackClick({ label: linkProps?.label, extraData: { moduleName: 'Carousel lead video link' } });
    linkProps?.onClick?.(e);
  };

  return (
    <div className={styles.wrapper}>
      {linkProps ? <GoToLink {...linkProps} className={styles.link} onClick={handleLinkClick} /> : null}
      <div className={styles.container}>
        {video ? (
          <div className={styles.leadContainer}>
            <AspectRatio ratio={isDesktop ? ASPECT_RATIOS.portrait : ASPECT_RATIOS.landscape}>
              <YoutubePlayer
                {...contentfulYoutubeToYoutubePlayerProps(video)}
                orientation={isDesktop ? 'Portrait' : 'Landscape'}
                onStateChange={handleVideoPlayPause}
                desktopThumbnail={desktopCoverImage}
                mobileThumbnail={mobileCoverImage}
              />
            </AspectRatio>
          </div>
        ) : null}
        <div className={styles.carouselContainer}>
          <ProductCarousel {...productCarouselProps} />
        </div>
      </div>
      {ctaButton ? <div className={styles.ctaButton}>{ctaButton}</div> : null}
    </div>
  );
};
